import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import {
  X,
  Minus,
  Plus,
  Trash2,
  ShoppingBag,
  ArrowLeft,
  CheckCircle2,
  UtensilsCrossed,
  ShoppingBag as TakeawayIcon,
  Truck,
  Store,
  MapPin,
} from "lucide-react";
import { useCart } from "@/context/CartContext";
import { scrollToSection } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-zinc-100 placeholder-zinc-500 outline-none transition-colors focus:border-[#F57C00]/60";

const CartDrawer = () => {
  const { items, add, dec, removeItem, clear, total, drawerOpen, setDrawerOpen } = useCart();
  const [step, setStep] = useState("cart");
  const [form, setForm] = useState({
    customer_name: "",
    phone: "",
    order_type: "dine_in",
    delivery_zone: "pickup",
    table_number: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState(null);
  const [config, setConfig] = useState({ upi_id: "", upi_payee: "Podi Sambaram", delivery_fee_beyond_3km: 0 });

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const deliveryFee =
    form.order_type === "takeaway" && form.delivery_zone === "beyond_3km"
      ? config.delivery_fee_beyond_3km || 0
      : 0;
  const grandTotal = total + deliveryFee;

  const upiUrl = useMemo(() => {
    if (!order || !config.upi_id) return "";
    const params = new URLSearchParams({
      pa: config.upi_id,
      pn: config.upi_payee,
      am: String(order.total),
      cu: "INR",
      tn: order.order_no,
    });
    return `upi://pay?${params.toString()}`;
  }, [order, config]);

  const close = () => {
    setDrawerOpen(false);
    if (order) {
      setOrder(null);
      setStep("cart");
    }
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({ name: i.name, qty: i.qty })),
      };
      const res = await axios.post(`${API}/orders`, payload);
      setOrder(res.data);
      clear();
      setStep("payment");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error("Could not place your order", {
        description:
          typeof detail === "string"
            ? detail
            : "Please try again or call us at +91 92176 12215.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            data-testid="cart-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 260 }}
            className="fixed right-0 top-0 z-[95] flex h-full w-full max-w-md flex-col border-l border-white/10 bg-[#121215]"
          >
            <div className="flex items-center justify-between border-b border-white/8 px-6 py-5">
              <div className="flex items-center gap-3">
                {step === "checkout" && (
                  <button
                    data-testid="cart-back-button"
                    onClick={() => setStep("cart")}
                    className="rounded-full border border-white/10 p-1.5 text-zinc-300 hover:text-[#FF9800]"
                    aria-label="Back to cart"
                  >
                    <ArrowLeft size={15} />
                  </button>
                )}
                <h3 className="font-display text-xl font-semibold">
                  {step === "cart" && "Your Order"}
                  {step === "checkout" && "Checkout"}
                  {step === "payment" && "Scan & Pay"}
                </h3>
              </div>
              <button
                data-testid="cart-close-button"
                onClick={close}
                className="rounded-full border border-white/10 p-2 text-zinc-300 transition-colors hover:text-[#FF9800]"
                aria-label="Close cart"
              >
                <X size={16} />
              </button>
            </div>

            {step === "cart" && (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-5">
                  {items.length === 0 ? (
                    <div data-testid="cart-empty" className="flex h-full flex-col items-center justify-center gap-4 text-center">
                      <ShoppingBag size={40} className="text-zinc-600" />
                      <p className="text-sm text-zinc-400">Your plate is empty.</p>
                      <button
                        data-testid="cart-browse-menu-btn"
                        onClick={() => {
                          setDrawerOpen(false);
                          scrollToSection("#menu");
                        }}
                        className="rounded-full bg-[#F57C00] px-6 py-2.5 font-grotesk text-sm font-semibold text-black hover:bg-[#FF9800]"
                      >
                        Browse the Menu
                      </button>
                    </div>
                  ) : (
                    <ul className="space-y-4">
                      {items.map((item) => (
                        <li
                          key={item.name}
                          data-testid={`cart-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="card-surface flex gap-3 rounded-2xl p-3"
                        >
                          <img
                            src={item.image}
                            alt={item.name}
                            className="h-16 w-16 shrink-0 rounded-xl object-cover"
                          />
                          <div className="flex min-w-0 flex-1 flex-col justify-between">
                            <div className="flex items-start justify-between gap-2">
                              <p className="truncate font-grotesk text-sm font-semibold text-zinc-100">
                                {item.name}
                              </p>
                              <button
                                data-testid={`cart-remove-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                onClick={() => removeItem(item.name)}
                                className="text-zinc-500 transition-colors hover:text-red-400"
                                aria-label={`Remove ${item.name}`}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="font-mono-accent text-sm font-semibold text-[#FFB74D]">
                                ₹{item.price * item.qty}
                              </span>
                              <div className="flex items-center gap-3 rounded-full border border-white/10 px-2 py-1">
                                <button
                                  data-testid={`cart-dec-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                  onClick={() => dec(item.name)}
                                  className="text-zinc-300 hover:text-[#FF9800]"
                                  aria-label="Decrease quantity"
                                >
                                  <Minus size={13} />
                                </button>
                                <span className="font-mono-accent text-sm font-semibold">{item.qty}</span>
                                <button
                                  data-testid={`cart-inc-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                  onClick={() => add(item)}
                                  className="text-zinc-300 hover:text-[#FF9800]"
                                  aria-label="Increase quantity"
                                >
                                  <Plus size={13} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {items.length > 0 && (
                  <div className="border-t border-white/8 px-6 py-5">
                    <div className="flex items-center justify-between text-sm text-zinc-400">
                      <span>Total (no taxes, no hidden charges)</span>
                      <span data-testid="cart-total" className="font-mono-accent text-lg font-semibold text-[#FFB74D]">
                        ₹{total}
                      </span>
                    </div>
                    <button
                      data-testid="cart-checkout-button"
                      onClick={() => setStep("checkout")}
                      className="mt-4 w-full rounded-full bg-[#F57C00] py-3.5 font-grotesk text-sm font-semibold text-black transition-all hover:bg-[#FF9800]"
                    >
                      Proceed to Checkout
                    </button>
                  </div>
                )}
              </>
            )}

            {step === "checkout" && (
              <form
                data-testid="checkout-form"
                onSubmit={placeOrder}
                className="flex flex-1 flex-col overflow-y-auto px-6 py-5"
              >
                <div className="mb-4 grid grid-cols-2 gap-2">
                  {[
                    { value: "dine_in", label: "Dine-In", icon: UtensilsCrossed },
                    { value: "takeaway", label: "Takeaway / Delivery", icon: TakeawayIcon },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      data-testid={`checkout-type-${opt.value}`}
                      onClick={() => setForm({ ...form, order_type: opt.value })}
                      className={`flex items-center justify-center gap-2 rounded-xl border py-3 font-grotesk text-sm font-semibold transition-all ${
                        form.order_type === opt.value
                          ? "border-[#F57C00] bg-[#F57C00]/15 text-[#FF9800]"
                          : "border-white/10 text-zinc-400 hover:text-zinc-200"
                      }`}
                    >
                      <opt.icon size={15} /> {opt.label}
                    </button>
                  ))}
                </div>

                {form.order_type === "takeaway" && (
                  <div className="mb-4 space-y-2">
                    {[
                      { value: "pickup", label: "Self pickup at outlet", sub: "Free", icon: Store },
                      { value: "within_3km", label: "Home delivery within 3 KM", sub: "Free", icon: Truck },
                      {
                        value: "beyond_3km",
                        label: "Home delivery beyond 3 KM",
                        sub: `+₹${config.delivery_fee_beyond_3km || 0} delivery fee`,
                        icon: MapPin,
                      },
                    ].map((z) => (
                      <button
                        key={z.value}
                        type="button"
                        data-testid={`checkout-zone-${z.value.replace(/_/g, "-")}`}
                        onClick={() => setForm({ ...form, delivery_zone: z.value })}
                        className={`flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-all ${
                          form.delivery_zone === z.value
                            ? "border-[#F57C00] bg-[#F57C00]/10"
                            : "border-white/10 hover:border-white/25"
                        }`}
                      >
                        <span className="flex items-center gap-3">
                          <z.icon size={16} className={form.delivery_zone === z.value ? "text-[#FF9800]" : "text-zinc-500"} />
                          <span className="font-grotesk text-sm font-medium text-zinc-100">{z.label}</span>
                        </span>
                        <span className={`font-mono-accent text-xs font-semibold ${z.value === "beyond_3km" ? "text-amber-300" : "text-[#81C784]"}`}>
                          {z.sub}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                <div className="space-y-4">
                  <input
                    data-testid="checkout-name-input"
                    required
                    value={form.customer_name}
                    onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                    placeholder="Your name"
                    className={inputCls}
                  />
                  <input
                    data-testid="checkout-phone-input"
                    required
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="Phone number"
                    className={inputCls}
                  />
                  {form.order_type === "dine_in" && (
                    <input
                      data-testid="checkout-table-input"
                      value={form.table_number}
                      onChange={(e) => setForm({ ...form, table_number: e.target.value })}
                      placeholder="Table number (optional)"
                      className={inputCls}
                    />
                  )}
                  {form.order_type === "takeaway" && form.delivery_zone !== "pickup" && (
                    <input
                      data-testid="checkout-address-input"
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Delivery address & landmark"
                      required
                      className={inputCls}
                    />
                  )}
                  {(form.order_type === "dine_in" || form.delivery_zone === "pickup") && (
                    <input
                      data-testid="checkout-notes-input"
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Notes for the kitchen (optional)"
                      className={inputCls}
                    />
                  )}
                </div>

                <div className="card-surface mt-5 rounded-2xl p-4">
                  <p className="mb-3 font-mono-accent text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    Bill Summary
                  </p>
                  <ul className="space-y-1.5 text-sm text-zinc-400">
                    {items.map((i) => (
                      <li key={i.name} className="flex justify-between gap-3">
                        <span className="truncate">
                          {i.qty} × {i.name}
                        </span>
                        <span className="font-mono-accent text-zinc-300">₹{i.price * i.qty}</span>
                      </li>
                    ))}
                    {deliveryFee > 0 && (
                      <li data-testid="checkout-delivery-fee" className="flex justify-between gap-3 text-amber-300">
                        <span>Delivery fee (beyond 3 KM)</span>
                        <span className="font-mono-accent">₹{deliveryFee}</span>
                      </li>
                    )}
                  </ul>
                  <div className="mt-3 flex justify-between border-t border-white/8 pt-3">
                    <span className="font-grotesk text-sm font-semibold text-zinc-200">
                      Total Payable
                    </span>
                    <span data-testid="checkout-total" className="font-mono-accent text-lg font-semibold text-[#FFB74D]">
                      ₹{grandTotal}
                    </span>
                  </div>
                </div>

                <button
                  data-testid="checkout-submit-button"
                  type="submit"
                  disabled={submitting || items.length === 0}
                  className="mt-5 w-full rounded-full bg-[#F57C00] py-3.5 font-grotesk text-sm font-semibold text-black transition-all hover:bg-[#FF9800] disabled:opacity-60"
                >
                  {submitting ? "Placing order..." : "Place Order & Get Payment QR"}
                </button>
              </form>
            )}

            {step === "payment" && order && (
              <div className="flex flex-1 flex-col items-center overflow-y-auto px-6 py-6 text-center">
                <CheckCircle2 size={40} className="text-[#4CAF50]" />
                <h4 className="mt-3 font-display text-2xl font-semibold">Order placed!</h4>
                <p className="mt-1 font-mono-accent text-sm text-[#FFB74D]" data-testid="payment-order-no">
                  {order.order_no}
                </p>

                <div className="mt-6 rounded-3xl bg-white p-5" data-testid="payment-qr-code">
                  {upiUrl && <QRCodeSVG value={upiUrl} size={200} level="M" includeMargin />}
                </div>

                <details className="mt-4 w-full max-w-xs rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-left">
                  <summary
                    data-testid="static-qr-toggle"
                    className="cursor-pointer font-grotesk text-xs font-medium text-zinc-300"
                  >
                    Trouble scanning? Use our merchant QR (enter ₹{order.total} manually)
                  </summary>
                  <img
                    src="/assets/payment-qr.png"
                    alt="Yes Bank UPI QR — VAAYU FOOD WORKS"
                    data-testid="static-payment-qr"
                    className="mx-auto mt-3 w-56 rounded-xl bg-white"
                  />
                </details>

                <p className="mt-5 font-mono-accent text-3xl font-bold text-[#F5F2EB]" data-testid="payment-amount">
                  ₹{order.total}
                </p>
                {order.delivery_fee > 0 && (
                  <p className="mt-1 font-mono-accent text-xs text-amber-300">
                    includes ₹{order.delivery_fee} delivery fee
                  </p>
                )}
                <p className="mt-1 font-mono-accent text-xs text-zinc-400">
                  UPI: {config.upi_id}
                </p>
                <p className="mt-4 max-w-xs text-sm leading-relaxed text-zinc-400">
                  Scan with GPay, PhonePe or Paytm to pay. Show order{" "}
                  <span className="text-[#FFB74D]">{order.order_no}</span> at the counter —
                  our team confirms your payment from the admin panel.
                </p>

                <button
                  data-testid="payment-done-button"
                  onClick={() => {
                    toast.success("Thank you!", {
                      description: `Order ${order.order_no} is with our kitchen. Payment will be confirmed shortly.`,
                    });
                    close();
                  }}
                  className="mt-6 w-full rounded-full bg-[#F57C00] py-3.5 font-grotesk text-sm font-semibold text-black transition-all hover:bg-[#FF9800]"
                >
                  Done
                </button>
                <a
                  data-testid="payment-track-link"
                  href="/track"
                  className="mt-4 font-grotesk text-xs font-medium text-[#FF9800] underline-offset-4 transition-colors hover:underline"
                >
                  Track this order live →
                </a>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
